import { useMemo, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { GrantableRole } from '../../../services/iam';

interface RolePickerProps {
    roles: GrantableRole[];
    isLoading: boolean;
    value: string;
    onChange: (roleKey: string) => void;
}

function matchesSearch(role: GrantableRole, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
        role.title.toLowerCase().includes(q) ||
        role.key.toLowerCase().includes(q) ||
        role.desc.toLowerCase().includes(q)
    );
}

// Search-then-pick list of role rows (title + desc), same visual language as
// PracticeScopeSearch/PracticeMultiSelect - a persistent list rather than a
// closed combobox, so the description is readable before picking.
export function RolePicker({ roles, isLoading, value, onChange }: RolePickerProps) {
    const [search, setSearch] = useState('');
    const filtered = useMemo(
        () => roles.filter((role) => matchesSearch(role, search)),
        [roles, search]
    );

    return (
        <div className="flex flex-col gap-2">
            <span className="h-10 border border-gray-300 rounded-lg px-3 flex items-center gap-2 bg-white focus-within:border-gray-800 focus-within:ring-2 focus-within:ring-gray-200">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search roles..."
                    className="flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
                />
            </span>

            <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 max-h-56 overflow-y-auto">
                {isLoading && (
                    <div className="px-3 py-3 text-[12px] text-gray-400">Loading roles…</div>
                )}
                {!isLoading && filtered.length === 0 && (
                    <div className="px-3 py-3 text-[12px] text-gray-400">No roles found.</div>
                )}
                {!isLoading &&
                    filtered.map((role) => {
                        const selected = role.key === value;
                        return (
                            <button
                                key={role.key}
                                type="button"
                                onClick={() => onChange(role.key)}
                                className={`w-full px-3 py-2.5 flex flex-col items-start gap-0.5 text-left transition-colors ${
                                    selected ? 'bg-gray-50' : 'hover:bg-gray-50'
                                }`}
                            >
                                <span className="flex items-center gap-2 w-full">
                                    <span className="text-[13px] font-semibold text-gray-800 truncate">
                                        {role.title}
                                    </span>
                                    {!role.grantable && (
                                        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                            unwired
                                        </span>
                                    )}
                                    {selected && (
                                        <CheckIcon className="h-3.5 w-3.5 text-gray-800 ml-auto shrink-0" />
                                    )}
                                </span>
                                <span className="text-[11px] text-gray-500 truncate w-full">
                                    {role.desc}
                                </span>
                            </button>
                        );
                    })}
            </div>
        </div>
    );
}
